export interface ChatDraftMessage {
  id: string;
  role: "agent" | "user" | "status";
  text: string;
  completed?: number;
  total?: number;
  state?: "queued" | "running" | "completed" | "failed" | "cancelled";
}

export interface ChatDraft {
  messages: ChatDraftMessage[];
  input: string;
  clientMessageId?: string;
  updatedAt: number;
}

const keyPrefix = "mezoniq-chat-draft:";
const maximumAgeMs = 14 * 24 * 60 * 60 * 1000;
const maximumMessages = 120;
const maximumTextLength = 4000;

const keyFor = (projectId: string) => `${keyPrefix}${projectId}`;

function parseDraft(value: string | null): ChatDraft | null {
  if (!value) return null;
  try {
    const candidate = JSON.parse(value) as Partial<ChatDraft>;
    if (
      !Array.isArray(candidate.messages) ||
      typeof candidate.input !== "string" ||
      typeof candidate.updatedAt !== "number" ||
      Date.now() - candidate.updatedAt > maximumAgeMs
    ) {
      return null;
    }
    const messages = candidate.messages.flatMap((message) => {
      if (
        !message ||
        typeof message.id !== "string" ||
        !["agent", "user", "status"].includes(message.role) ||
        typeof message.text !== "string"
      ) {
        return [];
      }
      return [
        {
          id: message.id,
          role: message.role,
          text: message.text.slice(0, maximumTextLength),
          ...(typeof message.completed === "number" ? { completed: message.completed } : {}),
          ...(typeof message.total === "number" ? { total: message.total } : {}),
          ...(typeof message.state === "string" ? { state: message.state } : {}),
        },
      ];
    });
    return {
      messages: messages.slice(-maximumMessages),
      input: candidate.input.slice(0, maximumTextLength),
      ...(typeof candidate.clientMessageId === "string"
        ? { clientMessageId: candidate.clientMessageId.slice(0, 120) }
        : {}),
      updatedAt: candidate.updatedAt,
    };
  } catch {
    return null;
  }
}

export function loadChatDraft(projectId: string): ChatDraft | null {
  try {
    return parseDraft(localStorage.getItem(keyFor(projectId)));
  } catch {
    return null;
  }
}

export function saveChatDraft(
  projectId: string,
  messages: ChatDraftMessage[],
  input: string,
  clientMessageId?: string,
): void {
  try {
    localStorage.setItem(
      keyFor(projectId),
      JSON.stringify({
        messages: messages.slice(-maximumMessages),
        input: input.slice(0, maximumTextLength),
        ...(clientMessageId ? { clientMessageId: clientMessageId.slice(0, 120) } : {}),
        updatedAt: Date.now(),
      } satisfies ChatDraft),
    );
  } catch {
    // Server-side project data remains authoritative when local storage is unavailable.
  }
}

export function clearChatDraft(projectId: string): void {
  try {
    localStorage.removeItem(keyFor(projectId));
  } catch {
    // Claiming the server-side project does not depend on local storage cleanup.
  }
}

export function subscribeToChatDraft(
  projectId: string,
  listener: (draft: ChatDraft) => void,
): () => void {
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== keyFor(projectId)) return;
    const draft = parseDraft(event.newValue);
    if (draft) listener(draft);
  };
  window.addEventListener("storage", handleStorage);
  return () => window.removeEventListener("storage", handleStorage);
}
